import { useCallback, useMemo, useState } from 'react';
import { Box, Button, Menu, MenuButton, MenuItem, MenuList, Text } from '@chakra-ui/react';
import PaginationComponent from '../../Pagination';
import NoDataComponent from '../../NoData';
import TableComponent from '../../Table';
import InputSearchComponent from '../../Common/Form/input-search';
import { formatDate } from '../../../utils/helper.util';
import ImageComponent from '../../Common/Image';
import ChevronDownIcon from '../../../assets/chevron-down-green-icon.svg';
import { useNavigate } from 'react-router-dom';
import SeedIcon from '../../../assets/seed-icon.svg';
import AvatarIcon from '../../../assets/medium_default_optimized.png';
import CommunityIcon from '../../../assets/community-icon.svg';
import EyeGreenIcon from '../../../assets/eye-light-green-icon.svg';
import InvestmentDetailsModal from '../../Modal/InvestmentDetailsModal';
import LearnMorePanel from '../../../routes/ArticleViewer/components/LearnMorePanel';
import NoResultImg from '../../../assets/no-result.svg';

const statusList = ['Active', 'Frozen'];

export const InvestmentsTableComponent = (props) => {
  const { dataTable, pagination, setPagination, refetch, totalPage, isFetching } = props;

  const navigate = useNavigate();

  const [detailArticle, setDetailArticle] = useState({});
  const [modal, setModal] = useState('');
  const [openLearnMorePanel, setOpenLearnMorePanel] = useState(false);
  const [status, setStatus] = useState('');

  const handleChangePage = (page) => {
    setPagination((pre) => {
      return {
        ...pre,
        page
      };
    });
  };

  const handleChangeSearch = (search) => {
    setPagination((pre) => {
      return {
        ...pre,
        page: 1,
        search: search
      };
    });
  };

  const convertArticle = useCallback((row) => {
    return (
      <Box
        display={'flex'}
        alignItems={'center'}
        width={{ base: '100vw', md: 'unset' }}
        cursor={'pointer'}
        onClick={() => navigate(`/articles/${row?.articleId}`)}
      >
        <Box position={'relative'} flexShrink={0}>
          {row?.article?.cover ? (
            <ImageComponent
              rounded={'12px'}
              w={'40px'}
              h={'40px'}
              src={row?.article?.cover}
              alt="cover"
            />
          ) : (
            <Box
              rounded={'12px'}
              w={'40px'}
              h={'40px'}
              bgColor={'#F2F3F7'}
              display={'flex'}
              justifyContent={'center'}
              alignItems={'center'}
              textAlign={'center'}
              fontSize={'10px'}
              lineHeight={'100%'}
            >
              No Image
            </Box>
          )}
        </Box>
        <Box ml={'12px'} display={'flex'}>
          <Text
            fontWeight={800}
            fontSize={'xs'}
            title={row?.article?.title}
            overflow={'hidden'}
            textOverflow={'ellipsis'}
            wordBreak={'break-word'}
            sx={{
              display: '-webkit-inline-box',
              WebkitLineClamp: '1',
              WebkitBoxOrient: 'vertical'
            }}
            _hover={{
              textDecoration: 'underline'
            }}
          >
            {row?.article?.title || 'Untitled'}
          </Text>
        </Box>
      </Box>
    );
  }, []);

  const convertWriter = useCallback(
    (row) => {
      return (
        <Box
          display={'flex'}
          alignItems={'center'}
          cursor={'pointer'}
          gap={2}
          onClick={() => navigate(`/${row?.article?.creator?.username}`)}
        >
          <img
            src={row?.article?.creator?.imgUrl || AvatarIcon}
            className="w-6 h-6 rounded-full object-cover flex-shrink-0"
            alt=""
          />
          <Box display={'flex'} flexDirection={'column'} minW={0}>
            <Text
              fontSize={'xs'}
              fontWeight={600}
              _hover={{
                color: '#4CAF50'
              }}
              title={row?.article?.creator?.firstName}
              isTruncated
              transition={'0.2s'}
            >
              {row?.article?.creator?.firstName}
            </Text>
            {row?.article?.creator?.username && (
              <span className="font-[600] text-[10px] leading-[12px] text-02-dim-gray">
                @{row?.article?.creator?.username}
              </span>
            )}
          </Box>
        </Box>
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const convertCurrentShare = useCallback((row) => {
    return (
      <Text color="#204A22" fontWeight={800} fontSize={'xs'}>
        {row.currentShare}% <span className="font-[400]"> of ad revenue pool </span>
      </Text>
    );
  }, []);

  const convertPointInvested = useCallback((row) => {
    return (
      <Box display={'flex'} alignItems={'center'} gap={1}>
        <img src={SeedIcon} className="w-5 h-5" alt="" />
        <Text fontSize={'xs'} fontWeight={800}>
          {row.pointInvested || 0}
        </Text>
      </Box>
    );
  }, []);

  const convertInvestors = useCallback((row) => {
    return (
      <Box display={'flex'} alignItems={'center'} gap={1}>
        <img src={CommunityIcon} className="w-5 h-5" alt="" />
        <Text fontSize={'xs'} fontWeight={600}>
          {row.article.totalInvestors || 0}{' '}
          {row.article.totalInvestors === 1 ? 'Investor' : 'Investors'}
        </Text>
      </Box>
    );
  }, []);

  const convertStatus = useCallback((row) => {
    return (
      <Box
        py={2}
        px={3}
        bgColor={row.status === 'Active' ? 'growth-green' : 'error-red'}
        rounded={'80px'}
        w={'fit-content'}
      >
        <Text fontSize={'xs'} color={'white'} fontWeight={800}>
          {row.status}
        </Text>
      </Box>
    );
  }, []);

  const convertAction = useCallback((row) => {
    return (
      <Box display="flex" alignItems={'center'}>
        <img
          src={EyeGreenIcon}
          className="cursor-pointer p-[10px] hover:opacity-80"
          alt="icon"
          title="View Investment Details"
          onClick={() => {
            setModal('investment-details');
            setDetailArticle(row);
          }}
        />
      </Box>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSort = (sortName) => {
    let objSort = {
      sortBy: pagination.sortBy,
      sort: pagination.sort
    };
    if (sortName !== objSort.sortBy) objSort = { sortBy: sortName, sort: 'asc' };
    else if (sortName === objSort.sortBy && objSort.sort === 'asc')
      objSort = {
        ...objSort,
        sort: 'desc'
      };
    else if (sortName === objSort.sortBy && objSort.sort === 'desc')
      objSort = {
        ...objSort,
        sort: 'asc'
      };
    setPagination({ ...pagination, ...objSort });
  };

  const columns = useMemo(
    () => [
      {
        Header: 'Article',
        accessor: (row) => convertArticle(row),
        id: 'article'
      },
      {
        Header: 'Writer',
        accessor: (row) => convertWriter(row),
        id: 'writer',
        widthCustom: '9rem',
        minWidthCustom: '9rem'
      },
      {
        Header: 'Current share',
        accessor: (row) => convertCurrentShare(row),
        id: 'currentShare',
        widthCustom: '11rem',
        minWidthCustom: '11rem',
        isSortField: true
      },
      {
        Header: 'Seeds invested',
        accessor: (row) => convertPointInvested(row),
        id: 'pointInvested',
        widthCustom: '10rem',
        minWidthCustom: '10rem',
        isSortField: true
      },
      {
        Header: 'Investors',
        accessor: (row) => convertInvestors(row),
        id: 'investors',
        widthCustom: '7rem',
        minWidthCustom: '7rem'
      },
      {
        Header: 'Status',
        accessor: (row) => convertStatus(row),
        id: 'status',
        widthCustom: '6rem',
        minWidthCustom: '6rem'
      },
      {
        Header: 'Date Invested',
        accessor: (row) => <Text fontSize={'xs'}>{formatDate(row?.createdAt, true)}</Text>,
        id: 'createdAt',
        widthCustom: '9rem',
        minWidthCustom: '9rem',
        isSortField: true
      },
      {
        Header: 'Actions',
        accessor: (row) => convertAction(row),
        widthCustom: '6rem',
        minWidthCustom: '6rem',
        id: 'action'
      }
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [convertWriter, convertCurrentShare, handleSort]
  );

  return (
    <Box
      w={'full'}
      border={'1px solid'}
      borderColor={'anti-flash-lite'}
      borderRadius={{ base: 0, md: '12px' }}
      shadow={'outline'}
    >
      <Box
        bg={'white'}
        p={{ base: '16px', md: '16px 24px' }}
        display={'flex'}
        alignItems={'start'}
        minH={'72px'}
        borderTopRadius={'12px'}
        justifyContent={'space-between'}
        overflow={'auto'}
      >
        <Box
          display={'flex'}
          gap="12px"
          flexDirection={{ base: 'column', md: 'row' }}
          width={'full'}
          overflow={'auto'}
        >
          <InputSearchComponent
            setPaginationSearch={handleChangeSearch}
            placeholder={'Search'}
            setLoading={() => {}}
            width={{ base: '100%', md: '320px' }}
          />
          <Menu>
            <MenuButton
              as={Button}
              rightIcon={<img src={ChevronDownIcon} alt="icon" />}
              w={'fit-content'}
              height={'36px'}
              variant={'growth-green-solid'}
              px={'18px'}
            >
              <Text color={'#4CAF50'} fontWeight={900} fontSize={'sm'} textAlign={'start'}>
                {status ? status : 'Status'}
              </Text>
            </MenuButton>
            <MenuList
              p={'0.5rem'}
              borderRadius={'0.5rem'}
              minW={'171px'}
              maxH={'300px'}
              overflow={'auto'}
              css={{
                '&::-webkit-scrollbar': {
                  width: '6px'
                },
                '&::-webkit-scrollbar-thumb': {
                  background: '#e6e7eb',
                  borderRadius: '30px'
                }
              }}
              shadow={'md'}
              border={'1px solid'}
              borderColor={'light-gray'}
            >
              {['All Status', ...statusList].map((item, index) => (
                <MenuItem
                  key={index}
                  p={'8px 8px 8px 10px'}
                  _focus={{ bgColor: 'white' }}
                  _hover={{
                    bgColor: 'anti-flash-white',
                    '& .child-box': {
                      display: 'block'
                    }
                  }}
                  onClick={() => {
                    setStatus(item);
                    if (item === 'All Status') {
                      setPagination({ ...pagination, status: undefined, page: 1 });
                    } else setPagination({ ...pagination, status: item, page: 1 });
                  }}
                  rounded={'0.5rem'}
                  fontSize={'14px'}
                  lineHeight={'20px'}
                  fontWeight={'800'}
                >
                  {item}
                </MenuItem>
              ))}
            </MenuList>
          </Menu>
        </Box>
      </Box>
      <Box
        bg={'white'}
        rounded={'0 0 0.75rem 0.75rem'}
        display={'flex'}
        minH={'370px'}
        flexFlow={'column'}
        overflowY={'hidden'}
        overflowX={'auto'}
        css={{
          '&::-webkit-scrollbar': {
            display: 'block',
            height: '8px' // For horizontal scrollbar
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#e6e7eb',
            borderRadius: '30px'
          },
          '&::-webkit-scrollbar-track': {
            background: '#f5f5f5'
          }
        }}
      >
        <TableComponent
          isLoading={isFetching}
          columns={columns}
          data={dataTable ?? []}
          headerHeight={'46px'}
          fontsizeTable={'sm'}
          handleSort={handleSort}
          currentSort={{ sortBy: pagination.sortBy, sort: pagination.sort }}
          haveFooter={false}
        />
        {!pagination.search && dataTable?.length === 0 && !isFetching ? (
          <Box minH={'370px'} display={'flex'} alignItems={'center'} justifyContent={'center'}>
            <NoDataComponent
              title="It looks like you haven’t made any investments yet."
              description="Start by investing in an article to support creators and share in their success!"
              titleButton="Discover Articles"
              onClick={() => navigate('/')}
              isFetching={isFetching}
            />
          </Box>
        ) : null}
        {pagination.search && dataTable?.length === 0 && !isFetching ? (
          <Box minH={'370px'} display={'flex'} alignItems={'center'} justifyContent={'center'}>
            <NoDataComponent
              icon={NoResultImg}
              title="No results found"
              description="Try a different search term"
              isFetching={isFetching}
            />
          </Box>
        ) : null}
      </Box>

      {totalPage > 0 ? (
        <Box
          borderBottomRadius={'12px'}
          p={'1.25rem 1.5rem'}
          mt={'auto'}
          borderTop="1px solid"
          borderColor={'anti-flash-lite'}
          background={'light.100'}
        >
          <PaginationComponent
            currentPage={pagination.page}
            totalPages={totalPage}
            onChangePage={handleChangePage}
          />
        </Box>
      ) : null}
      {modal === 'investment-details' ? (
        <InvestmentDetailsModal
          data={detailArticle}
          refetch={refetch}
          setOpenLearnMorePanel={setOpenLearnMorePanel}
          isOpen
          onClose={() => setModal('')}
        />
      ) : null}
      {openLearnMorePanel && <LearnMorePanel isOpen onClose={() => setOpenLearnMorePanel(false)} />}
    </Box>
  );
};
